import { api } from '@cwd/lib/main';
import {
    type EventParticipant,
    type ParticipantsResult,
    type ParticipantStatusName,
    type StatusFilter,
    type OrderByField,
    type OrderDirection,
} from './guest-list-types';
import { type CompleteSurveyResponse, type ParticipantHistoryEntry, type ResponseSurveyAnswer } from './guest-response-types';

export interface ParticipantsQuery {
    top: number;
    skip: number;
    status: StatusFilter;
    orderBy: OrderByField;
    orderDirection: OrderDirection;
    freeText: string;
}

const statusFilterClause = (status: StatusFilter) => (status ? `$filter=Status eq '${status}'` : '');

export async function loadParticipants(eventId: string, query: ParticipantsQuery, signal?: AbortSignal): Promise<ParticipantsResult> {
    const parts = [
        '$inlinecount=allpages',
        `$top=${query.top}`,
        `$skip=${query.skip}`,
        statusFilterClause(query.status),
        `$orderby=${query.orderBy}+${query.orderDirection}`,
        `freeTextFilter=${encodeURIComponent(query.freeText)}`,
    ].filter(Boolean);

    return api.get(`/Events/${eventId}/Participants?${parts.join('&')}`, { signal });
}

export async function loadParticipantsWithChildren(eventId: string, parentEventParticipantIds: string[], signal?: AbortSignal): Promise<EventParticipant[]> {
    return api.post(`/Events/${eventId}/GetEventParticipantsWithChildren`, { parentEventParticipantIds }, undefined, undefined, signal);
}

export async function participantExistsForContact(eventId: string, contactId: string): Promise<boolean> {
    const result: ParticipantsResult = await api.get(
        `/Events/${eventId}/Participants?$inlinecount=allpages&$top=1&$skip=0&$filter=ContactId eq guid'${contactId}'`
    );
    return result.Count > 0;
}

export async function addNewParticipant(eventId: string, participant: { Email: string | null; SmsNumber: string | null }): Promise<void> {
    return api.post(`/Events/${eventId}/AddNewParticipant`, participant);
}

export async function updateParticipantStatus(
    eventId: string,
    eventParticipantId: string,
    status: ParticipantStatusName,
    suppressNotifications: boolean
): Promise<void> {
    return api.post(`/Events/${eventId}/UpdateParticipantStatus/${eventParticipantId}/${status}`, suppressNotifications);
}

export async function statusToAttended(eventId: string, eventParticipantIds: string[]): Promise<void> {
    return api.post(`/Events/${eventId}/StatusToAttended`, eventParticipantIds);
}

export async function bulkStatusChange(
    eventId: string,
    request: { TargetStatus: ParticipantStatusName; SourceStatusFilter: ParticipantStatusName; FreeTextFilter: string }
): Promise<{ count: number }> {
    return api.post(`/Events/${eventId}/BulkStatusChange`, request);
}

export async function sendConfirmationMail(eventId: string, eventParticipantId: string, status: ParticipantStatusName): Promise<void> {
    return api.post(`/Events/${eventId}/Participants/${eventParticipantId}/SendConfirmationMail`, status);
}

export async function sendAttestationMail(eventId: string, eventParticipantId: string): Promise<void> {
    return api.post(`/Events/${eventId}/Participants/${eventParticipantId}/SendAttestationMail`);
}

export async function sendMultipleAttestationMail(eventId: string): Promise<void> {
    return api.post(`/Events/${eventId}/SendMultipleAttestationMail`);
}

export async function loadSurveyResponse(surveyResponseId: string): Promise<CompleteSurveyResponse> {
    return api.get(`/Events/SurveyResponse/${surveyResponseId}`);
}

export async function loadParticipantHistory(eventParticipantId: string): Promise<ParticipantHistoryEntry[]> {
    return api.get(`/Events/SurveyResponse/History/${eventParticipantId}`);
}

export async function createSurveyAnswer(surveyResponseId: string, eventParticipantId: string, answer: ResponseSurveyAnswer): Promise<void> {
    return api.post(`/Events/SurveyAnswer/Create/${surveyResponseId}/${eventParticipantId}`, answer);
}

export async function updateSurveyAnswer(eventParticipantId: string, answer: ResponseSurveyAnswer): Promise<void> {
    return api.post(`/Events/SurveyAnswer/Update/${eventParticipantId}`, answer);
}

// Exports only honour the status filter, not the free-text search (parity with the legacy guest list).

export async function exportParticipantsToFile(eventId: string, status: StatusFilter): Promise<{ Url: string }> {
    return api.get(`/Events/${eventId}/ParticipantExport?status=${status}`);
}

export async function exportParticipantsToCsv(eventId: string, status: StatusFilter): Promise<{ Url: string }> {
    return api.get(`/Events/${eventId}/Participants?${['$format=csv', statusFilterClause(status)].filter(Boolean).join('&')}`);
}

export async function exportParticipantsToNewList(eventId: string, status: StatusFilter): Promise<{ ListId: string }> {
    const result: { ListId: string } = await api.get(`/Events/${eventId}/Participants?${['$format=list', statusFilterClause(status)].filter(Boolean).join('&')}`);
    await api.post(`/Events/${eventId}/UpdateExportList/${result.ListId}`);
    return result;
}

export async function exportParticipantsToExistingList(eventId: string, status: StatusFilter, targetListId: string): Promise<{ ListId: string }> {
    const parts = ['$format=addtolist', `targetListId=${targetListId}`, statusFilterClause(status)].filter(Boolean);
    return api.get(`/Events/${eventId}/Participants?${parts.join('&')}`);
}
